/* eslint-disable react/prop-types */
/**
 * Admin → AOIs — maintain the Area-of-Interest master (BD.Admin).
 *
 * AOIs are the vocabulary behind the deal-level `aoi_id` field (the Area of
 * Interest picker on the deal-detail form, which reads the active-only
 * `listAois` list). Until de-hub migrations 069-071 the `aois` table had no
 * write path at all — it was populated by hand and by the 52-row bulk load in
 * migration 070, so adding one prospect meant writing SQL. This screen is that
 * missing path: add an AOI, rename it, set/clear its upstream prospect key, and
 * activate / deactivate (soft delete). There is NO hard delete, because deals
 * reference AOIs by id and history must stay intact.
 *
 * Deactivating flips `active=false`: the row stays listed here (greyed + tagged
 * INACTIVE) so an admin can reactivate it, but it drops out of the active-only
 * AOI picker. A Popconfirm guards deactivation; reactivation is a single click.
 *
 * PROSPECT KEY (SRC_ID)
 * An AOI may carry a `src_id` — the `PR`-prefixed key it came over with from the
 * upstream internal system of record (de-hub migration 069). It is genuinely
 * optional and splits the master cleanly: the 43 `… PROSPECT` rows each have
 * one, while the 9 `… AOI` acreage groupings (HELIOS, KNOX, PERMIAN LESS
 * HELIOS, …) are defined internally and nothing upstream issues them a key.
 * The key is provenance, not identity — it must be unique when set, but the
 * NAME is what users see and pick.
 *
 * WHY DUPLICATE NAMES ARE BLOCKED HERE (unlike Operators)
 * The Operators screen only WARNS about a duplicate name, because an operator is
 * told apart by its code and the upstream master really does hold eight names
 * twice. An AOI has no such disambiguator — the picker shows the name alone —
 * so a look-alike is always a mistake. The server 409s (POST and PUT) and this
 * screen blocks the save before the round-trip, surfacing the row it clashes
 * with. Deactivated AOIs count: the fix for "it exists but is retired" is to
 * reactivate it, not to add a twin.
 *
 * Gating is UI/UX only — the de-hub `/aois` endpoints are the real boundary
 * (both POST and PUT are admin-only). We redirect non-admins to `/` as
 * defense-in-depth for the route, mirroring the Entities and Operators pages.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Tag,
  Typography,
  message,
  notification,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  StopOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import useMeasure from "react-use-measure";

import {
  WigAntLayout,
  WigAntTable,
} from "@warwick-investment-group/wig-react-library";

import SignIn from "../../signIn";
import { AppContext } from "../../../AppContextProvider";
import usePermissions from "../../../hooks/usePermissions";
import dealsService from "../../../services/dealsService";
import { apiErrorText } from "../../../utils/apiError";
import { findNameMatches } from "../../../utils/normalizeLookupName";

const { Title } = Typography;

// Empty form template — `name` and `src_id` are the editable fields.
const EMPTY_AOI = { name: "", src_id: "" };

/**
 * AoiFormModal — single Add/Edit modal. The parent owns visibility and the row
 * under edit; this component is layout + validation only.
 *
 * `existingRows` is the full AOI list from the parent, used to BLOCK a duplicate
 * name (the server would 409 anyway) and to name the row it collides with.
 */
const AoiFormModal = ({
  open,
  mode, // 'add' | 'edit'
  initialValues,
  saving,
  existingRows,
  editingId,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  // Mirror the name so the duplicate notice re-renders as the admin types.
  const typedName = Form.useWatch("name", form);

  // Re-seed the form whenever the modal opens or the row under edit changes.
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(initialValues || EMPTY_AOI);
    }
  }, [open, initialValues, form]);

  // AOIs already using the typed name (server-normalized comparison), excluding
  // the row being renamed. This mirrors the server's dedupe key, so a match
  // here is the 409 the save would return.
  const duplicateMatches = useMemo(
    () => findNameMatches(typedName, existingRows, editingId),
    [typedName, existingRows, editingId]
  );

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values, { setFieldError: form.setFields });
    } catch {
      // validation / onSubmit errors surfaced inline by AntD or by the parent.
    }
  };

  return (
    <Modal
      open={open}
      title={mode === "add" ? "Add AOI" : "Edit AOI"}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={saving}
      okText={mode === "add" ? "Create" : "Save"}
      destroyOnHidden
      width={520}
    >
      <Form form={form} layout="vertical" initialValues={EMPTY_AOI}>
        <Form.Item
          label="Name"
          name="name"
          rules={[
            { required: true, message: "Name is required" },
            // Blocking, unlike the Operators screen: the server rejects a
            // duplicate AOI name, and the picker shows the name alone so a
            // look-alike would be indistinguishable. Checking here just saves
            // the round-trip and gives a better message.
            {
              validator: () =>
                duplicateMatches.length === 0
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        `"${duplicateMatches[0].name}" already uses this name`
                      )
                    ),
            },
          ]}
          tooltip="Display name, e.g. HELIOS AOI or WARD PROSPECT. This is what the deal-detail Area of Interest picker shows, so it must be unique."
        >
          <Input autoFocus placeholder="e.g. HELIOS AOI" />
        </Form.Item>

        {/* The name is already blocked by the validator above; this explains
            WHY, and points at reactivation when the clash is a retired row. */}
        {duplicateMatches.length > 0 && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="An AOI already uses this name"
            description={
              <React.Fragment>
                <div style={{ marginBottom: 4 }}>
                  {duplicateMatches.some((row) => !row.active)
                    ? "AOI names must be unique — the picker shows the name alone, with no code to tell two apart. The match below is deactivated; reactivate it from the grid instead of adding a second one."
                    : "AOI names must be unique — the picker shows the name alone, with no code to tell two apart. Pick a different name, or edit the existing AOI."}
                </div>
                <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
                  {duplicateMatches.map((row) => (
                    <li key={row.id}>
                      {row.name}
                      {row.src_id ? (
                        <Typography.Text type="secondary">
                          {" "}
                          — key {row.src_id}
                        </Typography.Text>
                      ) : (
                        <Typography.Text type="secondary"> — no key</Typography.Text>
                      )}
                      {!row.active && (
                        <Tag color="default" style={{ marginLeft: 6 }}>
                          INACTIVE
                        </Tag>
                      )}
                    </li>
                  ))}
                </ul>
              </React.Fragment>
            }
          />
        )}

        <Form.Item
          label="Prospect key"
          name="src_id"
          tooltip="The AOI's key in the upstream internal system of record (e.g. PR0013133). Optional — the acreage groupings (HELIOS AOI, PERMIAN LESS HELIOS AOI, …) are defined internally and have none. Must be unique: reusing another AOI's key is rejected."
        >
          <Input placeholder="e.g. PR0013133 (optional)" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const Aois = () => {
  const navigate = useNavigate();
  const [ref, bounds] = useMeasure();
  const { token, getToken, isTokenExpired, user, theme } =
    useContext(AppContext);
  const { can } = usePermissions();
  const allowed = can("admin.aois");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' | 'edit'
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);

  // Redirect users who lack the permission (defense-in-depth for the route;
  // the API is the real boundary).
  useEffect(() => {
    if (!allowed) navigate("/", { replace: true });
  }, [allowed, navigate]);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    if (!userOid) throw new Error("OID not found in user claims.");
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg(null);
      const { token: tk, userOid } = await getAuth();
      const resp = await dealsService.listAllAois({
        token: tk,
        userOid,
        includeInactive,
      });
      const data = resp?.data?.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading AOIs:", err);
      setErrorMsg(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [getAuth, includeInactive]);

  useEffect(() => {
    if (allowed) refresh();
  }, [allowed, refresh]);

  // ---------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------

  const handleCreate = async (values, helpers) => {
    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      await dealsService.createAoi({
        token: tk,
        userOid,
        name: values.name?.trim(),
        // Blank is dropped from the body by the service — an acreage grouping
        // has no upstream key.
        srcId: values.src_id,
      });
      message.success("AOI created");
      setModalOpen(false);
      await refresh();
    } catch (err) {
      handleSaveError(err, helpers, values);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (values, helpers) => {
    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      // `src_id` is always sent so the field can CLEAR a key: an explicit null
      // is distinguishable server-side from an omitted key, while a blank
      // string normalizes to null there too.
      const key = values.src_id?.trim();
      await dealsService.updateAoi({
        token: tk,
        userOid,
        id: editingRow.id,
        body: { name: values.name?.trim(), src_id: key || null },
      });
      message.success("AOI saved");
      setModalOpen(false);
      await refresh();
    } catch (err) {
      handleSaveError(err, helpers, values);
    } finally {
      setSaving(false);
    }
  };

  // Surface a save error inline on the offending field, or as a top-level
  // notification.
  //
  // A 409 here can mean either of two things — the NAME normalizes onto an
  // existing AOI, or the PROSPECT KEY is already assigned — so unlike the
  // Operators screen (where 409 always means the code) we have to tell them
  // apart. The server's detail names the field's value, so we look for the key
  // we just sent in it; anything else is treated as a name clash, which is the
  // check the server runs first.
  const handleSaveError = (err, helpers, values) => {
    const status = err?.response?.status;
    const detail = apiErrorText(err, { fallback: "Save failed" });

    if (status === 409) {
      const sentKey = values?.src_id?.trim();
      const field =
        sentKey && String(detail).includes(sentKey) ? "src_id" : "name";
      helpers?.setFieldError?.([{ name: field, errors: [String(detail)] }]);
      return;
    }
    notification.error({
      message: "AOI save failed",
      description: String(detail),
    });
  };

  // Activate / deactivate — a soft toggle on `active`. No hard delete.
  const handleToggleActive = async (row) => {
    try {
      const { token: tk, userOid } = await getAuth();
      await dealsService.updateAoi({
        token: tk,
        userOid,
        id: row.id,
        body: { active: !row.active },
      });
      message.success(row.active ? "AOI deactivated" : "AOI activated");
      await refresh();
    } catch (err) {
      const detail = apiErrorText(err);
      notification.error({
        message: row.active ? "Failed to deactivate AOI" : "Failed to activate AOI",
        description: detail,
      });
    }
  };

  // ---------------------------------------------------------------------
  // Grid wiring
  // ---------------------------------------------------------------------

  const openAdd = () => {
    setModalMode("add");
    setEditingRow(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalMode("edit");
    setEditingRow(row);
    setModalOpen(true);
  };

  const fmtDate = (v) => {
    if (!v) return "";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
  };

  const columns = useMemo(
    () => [
      {
        dataIndex: "name",
        title: "Name",
        key: "name",
        sorter: (a, b) =>
          String(a?.name ?? "").localeCompare(String(b?.name ?? "")),
        render: (value, row) =>
          row?.active ? (
            value
          ) : (
            <span style={{ opacity: 0.55 }}>{value}</span>
          ),
      },
      {
        // The upstream prospect key. Informational — a blank cell is normal
        // (the acreage groupings have none), not missing data.
        dataIndex: "src_id",
        title: "Prospect key",
        key: "src_id",
        width: 140,
        sorter: (a, b) =>
          String(a?.src_id ?? "").localeCompare(String(b?.src_id ?? "")),
        render: (value, row) => {
          if (!value) {
            return (
              <Typography.Text type="secondary" italic>
                —
              </Typography.Text>
            );
          }
          return row?.active ? (
            value
          ) : (
            <span style={{ opacity: 0.55 }}>{value}</span>
          );
        },
      },
      {
        dataIndex: "active",
        title: "Active",
        key: "active",
        width: 110,
        sorter: (a, b) => Number(a?.active) - Number(b?.active),
        render: (value) => (
          <Tag color={value ? "green" : "default"}>
            {value ? "ACTIVE" : "INACTIVE"}
          </Tag>
        ),
      },
      {
        dataIndex: "created_by",
        title: "Created by",
        key: "created_by",
        width: 160,
        // Bulk-loaded rows (de-hub migration 070) predate the audit columns,
        // so an empty cell means "came from the load", not "unknown".
        render: (value) =>
          value || (
            <Typography.Text type="secondary" italic>
              bulk load
            </Typography.Text>
          ),
      },
      {
        dataIndex: "created_at",
        title: "Created",
        key: "created_at",
        width: 190,
        sorter: (a, b) =>
          String(a?.created_at ?? "").localeCompare(
            String(b?.created_at ?? "")
          ),
        render: (value) => fmtDate(value),
      },
      {
        dataIndex: "updated_by",
        title: "Updated by",
        key: "updated_by",
        width: 160,
      },
      {
        dataIndex: "updated_at",
        title: "Updated",
        key: "updated_at",
        width: 190,
        sorter: (a, b) =>
          String(a?.updated_at ?? "").localeCompare(
            String(b?.updated_at ?? "")
          ),
        render: (value) => fmtDate(value),
      },
      {
        title: "Actions",
        key: "actions",
        width: 130,
        fixed: "right",
        align: "center",
        render: (_, row) => {
          if (!row) return null;
          return (
            <Space size="small" onClick={(e) => e.stopPropagation()}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(row)}
                aria-label={`Edit ${row.name}`}
              />
              {row.active ? (
                <Popconfirm
                  title="Deactivate AOI?"
                  description={`Deactivate "${row.name}"? It stays on file (deals still reference it) but won't appear in the Area of Interest picker.`}
                  okText="Deactivate"
                  okType="danger"
                  cancelText="Cancel"
                  onConfirm={() => handleToggleActive(row)}
                >
                  <Button
                    size="small"
                    danger
                    icon={<StopOutlined />}
                    aria-label={`Deactivate ${row.name}`}
                  />
                </Popconfirm>
              ) : (
                <Button
                  size="small"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleToggleActive(row)}
                  aria-label={`Activate ${row.name}`}
                />
              )}
            </Space>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Initial values for the modal — for Edit we hydrate name + key from the row.
  // A null key becomes "" so the Input stays controlled and clearing it
  // round-trips as an explicit null on save.
  const modalInitialValues = useMemo(() => {
    if (modalMode === "edit" && editingRow) {
      return {
        name: editingRow.name ?? "",
        src_id: editingRow.src_id ?? "",
      };
    }
    return EMPTY_AOI;
  }, [modalMode, editingRow]);

  const gridHeight = Math.max(bounds.height - 220, 400);

  if (!allowed) return null;

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <Row ref={ref}>
          <WigAntLayout
            darkMode={theme === "dark"}
            layoutStyle={{
              height: "93vh",
              width: "99vw",
              margin: 0,
              padding: 0,
              overflow: "hidden",
            }}
            contentStyle={{
              minHeight: "70vh",
              overflow: "auto",
              padding: 12,
              margin: 0,
            }}
            showFooter={false}
            showHeader={false}
            siderProps={{ collapsible: true, collapsed: true, width: 0 }}
            showDefaultMenu={false}
            content={
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Row justify="space-between" align="middle">
                  <Title level={3} style={{ margin: 0 }}>
                    AOIs
                  </Title>
                  <Space>
                    <Checkbox
                      checked={includeInactive}
                      onChange={(e) => setIncludeInactive(e.target.checked)}
                    >
                      Show inactive
                    </Checkbox>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={openAdd}
                    >
                      Add AOI
                    </Button>
                  </Space>
                </Row>

                {errorMsg && (
                  <Alert
                    type="error"
                    showIcon
                    message="Failed to load AOIs"
                    description={errorMsg}
                  />
                )}

                {!loading && rows.length === 0 && !errorMsg ? (
                  <Alert
                    type="info"
                    showIcon
                    message="No AOIs yet"
                    description="There are no Area of Interest records. Click Add AOI to create the first one."
                  />
                ) : (
                  <WigAntTable
                    dataSource={rows}
                    columns={columns}
                    rowKey={(record) =>
                      record?.id != null ? String(record.id) : undefined
                    }
                    loading={loading}
                    showSearch
                    searchWidth={300}
                    showActionButton={false}
                    showExport
                    exportOptions={{ filename: "aois" }}
                    showPagination
                    pagination={{
                      defaultPageSize: 100,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "25", "50", "100"],
                    }}
                    tableWidth="100%"
                    scroll={{ y: gridHeight }}
                  />
                )}

                <AoiFormModal
                  open={modalOpen}
                  mode={modalMode}
                  initialValues={modalInitialValues}
                  saving={saving}
                  // The loaded list doubles as the duplicate-name lookup. It
                  // reflects the `includeInactive` toggle, so with inactive rows
                  // hidden the client check can only cite active AOIs — the
                  // server still 409s on a retired one, and the default is on.
                  existingRows={rows}
                  editingId={modalMode === "edit" ? editingRow?.id : undefined}
                  onCancel={() => setModalOpen(false)}
                  onSubmit={modalMode === "add" ? handleCreate : handleEdit}
                />
              </Space>
            }
          />
        </Row>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default Aois;
